import { useState, useEffect } from "react";
import { UseFormSetValue, UseFormTrigger } from "react-hook-form";
import { userService } from "../../services/userService";
import { logger } from "../../utils/logger";
import { useTenant } from "../../contexts/TenantContext";
import { RegisterFormData, SqueezeRegisterFormData } from "../../schemas/auth";
import { webActivityService } from "../../services/webActivityService";

// Create a union type that can be either RegisterFormData or SqueezeRegisterFormData
type FormDataType = RegisterFormData | SqueezeRegisterFormData;

interface UseContactAutoFillProps {
  contactId?: string;
  setValue: UseFormSetValue<FormDataType>;
  trigger: UseFormTrigger<FormDataType>;
}

interface UseContactAutoFillResult {
  isLoading: boolean;
  error: string | null;
  contactFound: boolean;
}

/**
 * Hook to auto-fill form fields with contact information when a contact_id is provided
 */
export const useContactAutoFill = ({
  contactId,
  setValue,
  trigger,
}: UseContactAutoFillProps): UseContactAutoFillResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contactFound, setContactFound] = useState(false);
  const [contactLoaded, setContactLoaded] = useState(false);
  const { config } = useTenant();

  useEffect(() => {
    const fetchContactInfo = async () => {
      // Only fetch once and only if we have a contact ID and config
      if (contactLoaded || !contactId || !config) return;

      try {
        setIsLoading(true);
        setError(null);

        // Use config.id as the organization ID
        const organizationId = config.id;

        logger.debug("Fetching contact information for auto-fill", {
          contactId,
          organizationId,
        });
        const contact = await userService.findContactById(
          contactId,
          organizationId
        );

        if (contact) {
          logger.debug("Contact found, auto-filling form", {
            contactId,
            hasFirstName: !!contact.firstName,
            hasLastName: !!contact.lastName,
            hasEmail: !!contact.email,
            hasPhone: !!contact.phoneNumber,
          });

          // Auto-fill form fields
          if (contact.firstName) setValue("firstName", contact.firstName);
          if (contact.lastName) setValue("lastName", contact.lastName);
          if (contact.email) setValue("email", contact.email);

          // Skip phone number auto-fill if it's 10000000000
          if (contact.phoneNumber && contact.phoneNumber !== "10000000000") {
            setValue("phoneNumber", contact.phoneNumber);
          } else if (contact.phoneNumber === "10000000000") {
            logger.debug("Skipping auto-fill for test phone number", {
              contactId,
            });
          }

          // Set guest data for web activity tracking
          webActivityService.setGuestData({
            contactId: contactId,
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            phoneNumber:
              contact.phoneNumber !== "10000000000"
                ? contact.phoneNumber
                : undefined,
          });

          // Trigger validation
          await trigger();
          setContactFound(true);
        } else {
          logger.debug("Contact not found for auto-fill", { contactId });
          // Clear guest data if contact not found
          webActivityService.setGuestData(null);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch contact information";
        logger.error("Error fetching contact information", {
          contactId,
          error: errorMessage,
        });
        setError(errorMessage);
      } finally {
        setIsLoading(false);
        setContactLoaded(true);
      }
    };

    fetchContactInfo();
  }, [contactId, setValue, trigger, contactLoaded, config]);

  return { isLoading, error, contactFound };
};

export default useContactAutoFill;
